import { STRUCTURED_FILE_EXTS, getFileExt } from '@shared/types'

/**
 * The Inside-Out upload step is split into two zones so each file family can be
 * validated and routed on its own (WI 2042). The split already existed in the
 * extension constants — these helpers make it the single source of truth and
 * keep the decision testable without rendering the component.
 */
export type UploadZone = 'roster' | 'context'

const DOCUMENT_EXTS = ['.pdf', '.docx', '.pptx']

// .json is accepted in the roster zone as a Talent Navigator export — like
// XLSX/CSV, it goes through the AI pipeline (PBI 2180). STRUCTURED_FILE_EXTS
// from @tn/shared intentionally excludes it: that list is shared with other
// call sites (see GenerationService's ROSTER_EXTS) that need to build their
// own json-inclusive set rather than have it added there directly.
const STRUCTURED_EXTS = [...STRUCTURED_FILE_EXTS, '.json'] as string[]

export const ZONE_EXTS: Record<UploadZone, string[]> = {
  roster: STRUCTURED_EXTS,
  context: DOCUMENT_EXTS,
}

// The roster message names JSON because ZONE_EXTS.roster accepts it. The WI 2042
// acceptance criteria quote the shorter "XLSX or CSV" wording, which predates
// JSON being accepted in this zone at all — the AC text is being updated to
// match rather than shipping a message that understates what is valid.
export const ZONE_REJECT_MESSAGE: Record<UploadZone, string> = {
  roster: 'Employee rosters must be an XLSX, CSV, or Talent Navigator JSON file',
  context: 'Supporting documents must be a PDF, DOCX, or PPTX file',
}

/** True when this zone accepts the file, judged by extension alone. */
export function zoneAccepts(fileName: string, zone: UploadZone): boolean {
  return ZONE_EXTS[zone].includes(getFileExt(fileName))
}

/**
 * Split an incoming batch into the files a zone accepts and the ones it turns
 * away. The caller reports `rejected` to the user — the single-zone version
 * dropped wrong-type files in silence.
 */
export function splitByZone<T extends { name: string }>(
  files: T[],
  zone: UploadZone
): { accepted: T[]; rejected: T[] } {
  const accepted: T[] = []
  const rejected: T[] = []
  for (const file of files) {
    (zoneAccepts(file.name, zone) ? accepted : rejected).push(file)
  }
  return { accepted, rejected }
}

